import React from 'react';
import { useAdminDashboard } from './hooks/useAdminDashboard';

// Componentes de layout
import AdminHeader from './components/admin/AdminHeader';
import StatsCards from './components/admin/StatsCards';
import PropertiesSection from './components/admin/PropertiesSection';

// Modales
import PropertyModal from './components/admin/PropertyModal';
import ManageInvitesModal from './components/admin/ManageInvitesModal';
import BidConditionsModal from './components/admin/BidConditionsModal';
import CloseLastChanceModal from './components/admin/CloseLastChanceModal';
import StartLastChanceModal from './components/admin/StartLastChanceModal';
import ArchivePropertyModal from './components/admin/ArchivePropertyModal';
import StartOpenBiddingWithFinalistsModal from './components/admin/StartOpenBiddingWithFinalistsModal';
import StartBlindBiddingModal from './components/admin/StartBlindBiddingModal';
import ActivateBlindActiveModal from './components/admin/ActivateBlindActiveModal';
import CloseBlindBiddingModal from './components/admin/CloseBlindBiddingModal';
import NotificationToast from './components/NotificationToast';

/**
 * Dashboard principal de administración
 * REFACTORIZADO: Orquestador ligero que delega lógica al hook y UI a componentes
 */
const AdminDashboard = ({
  properties,
  bids,
  invites,
  onSaveProperties,
  onSaveBids,
  onSaveInvites,
  onRefresh,
  loading = false
}) => {
  // Estado para notificaciones
  const [notification, setNotification] = React.useState(null);

  // Función para mostrar notificaciones
  const showNotification = (type, title, message, options = {}) => {
    setNotification({ type, title, message, persistent: options.persistent });

    if (!options.persistent) {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Hook con toda la lógica
  const {
    // Estados de UI
    showCreateProperty,
    setShowCreateProperty,
    editingProperty,
    setEditingProperty,
    saving,
    selectedBidForConditions,
    setSelectedBidForConditions,
    propertyToClose,
    setPropertyToClose,
    propertyToArchive,
    setPropertyToArchive,
    propertyToStartLastChance,
    setPropertyToStartLastChance,
    // Estados de fase ciega
    propertyToStartOpen,
    setPropertyToStartOpen,
    blindOffersForOpen,
    setBlindOffersForOpen,
    propertyToStartBlind,
    setPropertyToStartBlind,
    propertyToActivateBlindActive,
    setPropertyToActivateBlindActive,
    blindOffersForActivation,
    propertyToCloseBlind,
    setPropertyToCloseBlind,

    // Estado archivadas
    showArchived,
    toggleArchived,
    archivedProperties,
    loadingArchived,
    
    // Datos
    stats,
    displayedProperties,
    isLoadingDisplay,
    selectedProperty,
    setSelectedProperty,
    
    // Funciones de datos
    getPropertyBids,
    getPropertyInvites,
    
    // Handlers
    handleArchiveProperty,
    confirmArchive,
    handleUnarchiveProperty,
    handleStartLastChance,
    handleCloseLastChance,
    handleStartBlindBidding,
    confirmStartBlindBidding,
    handleActivateBlindActive,
    confirmActivateBlindActive,
    skipBlindActiveToClose,
    handleCloseBlindBidding,
    confirmCloseBlindBidding,
    handleStartOpenBidding,
    confirmStartOpenBidding,
    handleSaveProperty,
    handleSaveInvites,
    handleViewConditions,
  } = useAdminDashboard(
    properties,
    bids,
    invites,
    onSaveProperties,
    onSaveBids,
    onSaveInvites,
    loading,
    onRefresh,
    showNotification
  );

  // Loading inicial
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  // Verificar si hay propiedades (activas o archivadas)
  const hasProperties = properties.length > 0 || archivedProperties.length > 0 || showArchived;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-6">
        
        {/* Header con toggle y botones */}
        <AdminHeader
          showArchived={showArchived}
          onToggleArchived={toggleArchived}
          onCreateProperty={() => setShowCreateProperty(true)}
          saving={saving}
          loadingArchived={loadingArchived}
          hasProperties={hasProperties}
        />

        {/* Estadísticas */}
        <StatsCards 
          stats={stats} 
          properties={properties}
          showArchived={showArchived}
          archivedCount={archivedProperties.length}
        />

        {/* Sección de propiedades */}
        <PropertiesSection
          properties={displayedProperties}
          showArchived={showArchived}
          isLoading={isLoadingDisplay}
          getPropertyBids={getPropertyBids}
          getPropertyInvites={getPropertyInvites}
          onEdit={setEditingProperty}
          onArchive={handleArchiveProperty}
          onUnarchive={handleUnarchiveProperty}
          onManageInvites={setSelectedProperty}
          onViewConditions={handleViewConditions}
          onCloseLastChance={setPropertyToClose}
          onStartLastChance={setPropertyToStartLastChance}
          onStartBlindBidding={handleStartBlindBidding}
          onActivateBlindActive={handleActivateBlindActive}
          onCloseBlindBidding={handleCloseBlindBidding}
          onStartOpenBidding={handleStartOpenBidding}
          onToggleArchived={toggleArchived}
          onCreateProperty={() => setShowCreateProperty(true)}
        />

        {/* Overlay de saving */}
        {saving && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40">
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
              <p className="text-white text-center">Guardando cambios...</p>
            </div>
          </div>
        )}

        {/* Modales */}
        {showCreateProperty && (
          <PropertyModal
            properties={properties}
            onSave={handleSaveProperty}
            onClose={() => setShowCreateProperty(false)}
            saving={saving}
          />
        )}

        {editingProperty && (
          <PropertyModal
            property={editingProperty}
            properties={properties}
            bids={bids}
            onSave={handleSaveProperty}
            onClose={() => setEditingProperty(null)}
            saving={saving}
          />
        )}

        {selectedProperty && (
          <ManageInvitesModal
            property={selectedProperty}
            invites={invites}
            bids={bids}
            onSaveInvites={handleSaveInvites}
            onClose={() => setSelectedProperty(null)}
            saving={saving}
          />
        )}

        {selectedBidForConditions && (
          <BidConditionsModal
            bid={selectedBidForConditions}
            onClose={() => setSelectedBidForConditions(null)}
          />
        )}

        {propertyToStartLastChance && (
          <StartLastChanceModal
            property={propertyToStartLastChance}
            onConfirm={(startDateTime, duration) => handleStartLastChance(propertyToStartLastChance, startDateTime, duration)}
            onCancel={() => setPropertyToStartLastChance(null)}
          />
        )}

        {propertyToArchive && (
          <ArchivePropertyModal
            property={propertyToArchive}
            onConfirm={confirmArchive}
            onCancel={() => setPropertyToArchive(null)}
          />
        )}

        {/* ✅ NUEVO: Modal combinado para abrir subasta con selección de finalistas */}
        {propertyToStartOpen && (
          <StartOpenBiddingWithFinalistsModal
            property={propertyToStartOpen}
            blindOffers={blindOffersForOpen}
            onConfirm={confirmStartOpenBidding}
            onClose={() => {
              setPropertyToStartOpen(null);
              setBlindOffersForOpen([]);
            }}
          />
        )}

        {propertyToStartBlind && (
          <StartBlindBiddingModal
            property={propertyToStartBlind}
            onConfirm={(greenThreshold, yellowThreshold) => confirmStartBlindBidding(greenThreshold, yellowThreshold)}
            onCancel={() => setPropertyToStartBlind(null)}
          />
        )}

        {propertyToActivateBlindActive && (
          <ActivateBlindActiveModal
            property={propertyToActivateBlindActive}
            blindOffers={blindOffersForActivation}
            onConfirm={(duration) => confirmActivateBlindActive(duration)}
            onSkip={skipBlindActiveToClose}
            onCancel={() => setPropertyToActivateBlindActive(null)}
          />
        )}

        {propertyToCloseBlind && (
          <CloseBlindBiddingModal
            property={propertyToCloseBlind}
            onConfirm={confirmCloseBlindBidding}
            onCancel={() => setPropertyToCloseBlind(null)}
          />
        )}

        {propertyToClose && (
          <CloseLastChanceModal
            property={propertyToClose}
            onConfirm={() => handleCloseLastChance(propertyToClose)}
            onCancel={() => setPropertyToClose(null)}
          />
        )}

        {/* Notificación Toast */}
        {notification && (
          <NotificationToast
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
            persistent={notification.persistent}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;